/**
 * State and API calls for the help chat widget.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from '@/contexts/LanguageContext';
import { ApiError } from '@/services/apiClient';
import {
  deleteHelpConversation,
  getHelpUnread,
  listHelpConversations,
  openHelpConversation,
  sendHelpFeedback,
  sendHelpMessage,
  type HelpConversation,
  type HelpMessage,
} from '@/services/helpService';
import {
  HELP_CONVERSATION_KEY as CONVERSATION_KEY,
  HELP_OPEN_EVENT,
  HELP_OPEN_KEY as OPEN_KEY,
  readHelpSession as readSession,
  screenLabel,
  writeHelpSession as writeSession,
} from './helpChatEvents';

const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? '');
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

type HelpImage = { file: File; url: string };

function isNotFound(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 404) return false;
  const details = err.details as { error?: unknown; code?: unknown } | null | undefined;
  return err.code === 'not_found' || details?.error === 'not_found' || err.message === 'not_found';
}

/** API error codes with their own copy in the three languages. */
const ERROR_KEYS: Record<string, string> = {
  daily_cap: 'help.errorDailyCap',
  monthly_cap: 'help.errorMonthlyCap',
  model_failed: 'help.errorModelFailed',
  disabled: 'help.errorDisabled',
  rate_limited: 'help.errorRateLimited',
  manual_missing: 'help.errorGeneric',
};

/**
 * User-facing text for an API failure: a known code in the user's language; the per-minute limit
 * (429 without a code); the server's validation text on a 400; otherwise the generic copy.
 */
function errorText(err: unknown, t: (key: string) => string): string {
  if (err instanceof ApiError) {
    const details = err.details as { error?: unknown; message?: unknown } | null | undefined;
    const code = typeof details?.error === 'string' ? details.error : err.code;
    if (code && Object.prototype.hasOwnProperty.call(ERROR_KEYS, code)) return t(ERROR_KEYS[code]);
    if (err.status === 429) return t('help.errorRateLimited');
    if (err.status === 400) {
      if (typeof details?.message === 'string' && details.message) return details.message;
      if (typeof details?.error === 'string' && details.error) return details.error;
      if (err.message) return err.message;
    }
  }
  return t('help.errorGeneric');
}

export function useHelpChat() {
  const { t, language } = useTranslation();
  const location = useLocation();

  const [open, setOpenState] = useState(() => readSession(OPEN_KEY) === '1');
  const [view, setView] = useState<'conversation' | 'history'>('conversation');
  const [conversationId, setConversationIdState] = useState<string | null>(() =>
    readSession(CONVERSATION_KEY)
  );
  const [messages, setMessages] = useState<HelpMessage[]>([]);
  const [conversations, setConversations] = useState<HelpConversation[] | null>(null);
  const [text, setText] = useState('');
  const [image, setImage] = useState<HelpImage | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unread, setUnread] = useState<{ conversation_id: string; total: number } | null>(null);

  // Bumped whenever the active conversation changes, so late responses are ignored.
  const epochRef = useRef(0);
  const imageRef = useRef<HelpImage | null>(null);
  const inflightImageRef = useRef<HelpImage | null>(null);
  const mountedRef = useRef(true);
  const ratedRef = useRef<Set<string>>(new Set());
  // Key (conversation:total) of the unread notice already acted on, so it is handled once.
  const handledUnreadRef = useRef<string | null>(null);
  imageRef.current = image;

  const setOpen = useCallback((value: boolean) => {
    setOpenState(value);
    writeSession(OPEN_KEY, value ? '1' : null);
  }, []);

  const setConversationId = useCallback((id: string | null) => {
    setConversationIdState(id);
    writeSession(CONVERSATION_KEY, id);
  }, []);

  const releaseImage = useCallback((img: HelpImage | null) => {
    if (img) URL.revokeObjectURL(img.url);
  }, []);

  const clearImage = useCallback(() => {
    setImage((prev) => {
      releaseImage(prev);
      return null;
    });
  }, [releaseImage]);

  const attach = useCallback(
    (file: File | null | undefined) => {
      if (!file) return;
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        setError(t('help.errorImageType'));
        return;
      }
      if (file.size > MAX_IMAGE_BYTES) {
        setError(t('help.errorImageSize'));
        return;
      }
      setError(null);
      setImage((prev) => {
        releaseImage(prev);
        return { file, url: URL.createObjectURL(file) };
      });
    },
    [t, releaseImage]
  );

  const newConversation = useCallback(() => {
    epochRef.current += 1;
    setConversationId(null);
    setMessages([]);
    setError(null);
    setBusy(false);
    setLoading(false);
    setView('conversation');
  }, [setConversationId]);

  /** Checks for team replies; on failure keeps the previous value, silently. */
  const refreshUnread = useCallback(async () => {
    try {
      const res = await getHelpUnread();
      if (mountedRef.current) setUnread(res);
    } catch {
      // keep the previous value
    }
  }, []);

  const resume = useCallback(
    (id: string) => {
      epochRef.current += 1;
      const epoch = epochRef.current;
      setConversationId(id);
      setMessages([]);
      setError(null);
      setBusy(false);
      setLoading(true);
      setView('conversation');
      openHelpConversation(id)
        .then((thread) => {
          if (epochRef.current !== epoch) return;
          setMessages(thread.messages);
          setLoading(false);
          // The server marked the team's messages as read when it served the thread.
          void refreshUnread();
        })
        .catch((err: unknown) => {
          if (epochRef.current !== epoch) return;
          setLoading(false);
          if (err instanceof ApiError && err.status === 404) {
            newConversation();
            return;
          }
          setError(errorText(err, t));
        });
    },
    [newConversation, setConversationId, refreshUnread, t]
  );

  // Restore the conversation that was open before a page reload.
  useEffect(() => {
    const stored = readSession(CONVERSATION_KEY);
    if (stored) resume(stored);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // External "open the help chat" requests: with text, a fresh conversation with that draft and no
  // attached screenshot; without text, just open the panel on the current conversation and draft.
  useEffect(() => {
    const handler = (event: Event) => {
      const prefill = (event as CustomEvent<string>).detail ?? '';
      if (prefill) {
        newConversation();
        setText(prefill);
        clearImage();
      }
      setOpen(true);
    };
    window.addEventListener(HELP_OPEN_EVENT, handler);
    return () => window.removeEventListener(HELP_OPEN_EVENT, handler);
  }, [newConversation, setOpen, clearImage]);

  // Open the panel straight on the conversation the team replied in.
  const openUnread = useCallback(() => {
    if (!unread) return;
    handledUnreadRef.current = `${unread.conversation_id}:${unread.total}`;
    resume(unread.conversation_id);
    setOpen(true);
  }, [unread, resume, setOpen]);

  // A reply arriving while the panel is open: show it without a click (unless a send is in progress).
  const unreadId = unread?.conversation_id ?? null;
  const unreadTotal = unread?.total ?? 0;
  useEffect(() => {
    if (!unreadId) {
      handledUnreadRef.current = null;
      return;
    }
    if (!open || busy) return;
    const key = `${unreadId}:${unreadTotal}`;
    if (handledUnreadRef.current === key) return;
    handledUnreadRef.current = key;
    resume(unreadId);
  }, [unreadId, unreadTotal, open, busy, resume]);

  const showHistory = useCallback(() => {
    setView('history');
    setError(null);
    listHelpConversations()
      .then((res) => setConversations(res.conversations))
      .catch((err: unknown) => {
        setConversations((prev) => prev ?? []);
        setError(errorText(err, t));
      });
  }, [t]);

  const send = useCallback(
    async (question: string) => {
      const trimmed = question.trim();
      const pendingImage = imageRef.current;
      if ((!trimmed && !pendingImage) || busy || loading) return;

      const epoch = epochRef.current;
      const optimistic: HelpMessage = {
        id: `tmp-${Date.now()}`,
        role: 'user',
        text: trimmed,
        image_url: pendingImage?.url ?? null,
        created_at: new Date().toISOString(),
        feedback: null,
      };

      setError(null);
      setBusy(true);
      setMessages((prev) => [...prev, optimistic]);
      setText('');
      setImage(null);
      inflightImageRef.current = pendingImage;

      const finishImage = () => {
        inflightImageRef.current = null;
      };

      try {
        const imageBase64 = pendingImage ? await fileToBase64(pendingImage.file) : undefined;
        const post = (conversation_id: string | null) =>
          sendHelpMessage({
            conversation_id,
            message: trimmed,
            ...(pendingImage ? { image_base64: imageBase64, image_mime: pendingImage.file.type } : {}),
            screen: screenLabel(location.pathname),
            language,
          });

        let thread;
        try {
          thread = await post(conversationId);
        } catch (err) {
          // The conversation was deleted elsewhere: start a new one and retry once.
          if (conversationId && isNotFound(err) && epochRef.current === epoch) {
            setConversationId(null);
            thread = await post(null);
          } else {
            throw err;
          }
        }
        finishImage();
        releaseImage(pendingImage);
        if (!mountedRef.current || epochRef.current !== epoch) return;
        setConversationId(thread.conversation.id);
        setMessages(thread.messages);
      } catch (err) {
        finishImage();
        if (!mountedRef.current || epochRef.current !== epoch) {
          releaseImage(pendingImage);
          return;
        }
        setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
        setText(trimmed);
        setImage((prev) => {
          if (prev && prev !== pendingImage) releaseImage(prev);
          return pendingImage;
        });
        setError(errorText(err, t));
      } finally {
        if (mountedRef.current && epochRef.current === epoch) setBusy(false);
      }
    },
    [busy, loading, conversationId, language, location.pathname, releaseImage, setConversationId, t]
  );

  const remove = useCallback(
    async (id: string) => {
      try {
        const res = await deleteHelpConversation(id);
        setConversations(res.conversations);
        if (id === conversationId) {
          epochRef.current += 1;
          setConversationId(null);
          setMessages([]);
          setBusy(false);
          setLoading(false); // a resume still in flight for this conversation is now ignored
        }
      } catch (err) {
        setError(errorText(err, t));
      }
    },
    [conversationId, setConversationId, t]
  );

  const rate = useCallback(
    async (messageId: string, value: 1 | -1) => {
      if (messageId.startsWith('tmp-')) return;
      if (ratedRef.current.has(messageId)) return;
      ratedRef.current.add(messageId);
      setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, feedback: value } : m)));
      try {
        await sendHelpFeedback(messageId, value);
      } catch (err) {
        ratedRef.current.delete(messageId);
        setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, feedback: null } : m)));
        setError(errorText(err, t));
      }
    },
    [t]
  );

  // Release the object URL on unmount.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      releaseImage(imageRef.current);
      releaseImage(inflightImageRef.current);
    };
  }, [releaseImage]);

  return {
    open,
    setOpen,
    view,
    setView,
    conversationId,
    messages,
    conversations,
    text,
    setText,
    image,
    attach,
    clearImage,
    busy,
    loading,
    error,
    send,
    newConversation,
    showHistory,
    resume,
    remove,
    rate,
    unread,
    refreshUnread,
    openUnread,
  };
}
